import { makeStyles } from "@material-ui/core/styles";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableFooter from "@material-ui/core/TableFooter";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import ResponsiveTable from "@saleor/components/ResponsiveTable";
import Skeleton from "@saleor/components/Skeleton";
import TableCellHeader from "@saleor/components/TableCellHeader";
import TablePagination from "@saleor/components/TablePagination";
import { maybe, renderCollection } from "@saleor/misc";
import { SellerList_brandsPrivate_edges_node } from "@saleor/seller/types/SellerList";
import { SellerListUrlSortField } from "@saleor/seller/urls";
import { ListProps, SortPage } from "@saleor/types";
import { getArrowDirection } from "@saleor/utils/sort";
import React from "react";
import { FormattedMessage } from "react-intl";

const useStyles = makeStyles(
  theme => ({
    colAction: {
      width: "150px"
    },
    colCompany: {
      width: "300px"
    },
    colEmail: {
      width: "300px"
    },
    colLessee: {
      width: "300px"
    },
    colMobile: {
      width: "200px"
    },
    colRow: {
      "& th": {
        fontWeight: 600 as 600,
        paddingBottom: theme.spacing(1),
        paddingTop: theme.spacing(1)
      }
    },
    colSeller: {
      width: "120px"
    },
    colSignatory: {
      width: "200px"
    },
    colStatus: {
      width: "150px"
    },
    redButton: {
      "&:hover": {
        backgroundColor: theme.palette.error.dark
      },
      backgroundColor: theme.palette.error.main,
      color: theme.palette.error.contrastText
    },
    table: {
      tableLayout: "fixed"
    },
    tableContainer: {
      overflowX: "scroll"
    },
    tableRow: {
      cursor: "pointer"
    }
  }),
  { name: "MallList" }
);

interface SellerListProps extends ListProps, SortPage<SellerListUrlSortField> {
  sellers: SellerList_brandsPrivate_edges_node[];
}

const SellerList: React.FC<SellerListProps> = ({
  sellers,
  onNextPage,
  onPreviousPage,
  onUpdateListSettings,
  pageInfo,
  settings,
  disabled,
  onRowClick,
  onSort,
  sort
}) => {
  const colNumber = 8;
  const classes = useStyles({});
  return (
    <>
      <ResponsiveTable className={classes.table}>
        <TableHead>
          <TableRow className={classes.colRow}>
            <TableCellHeader className={classes.colSeller}>
              <FormattedMessage defaultMessage="Seller ID" />
            </TableCellHeader>
            <TableCellHeader
              direction={
                sort.sort === SellerListUrlSortField.name
                  ? getArrowDirection(sort.asc)
                  : undefined
              }
              arrowPosition="right"
              onClick={() => onSort(SellerListUrlSortField.name)}
              className={classes.colCompany}
            >
              <FormattedMessage defaultMessage="(Seller) Company Name" />
            </TableCellHeader>
            <TableCellHeader
              direction={
                sort.sort === SellerListUrlSortField.name
                  ? getArrowDirection(sort.asc)
                  : undefined
              }
              arrowPosition="right"
              onClick={() => onSort(SellerListUrlSortField.name)}
              className={classes.colLessee}
            >
              {" "}
              <FormattedMessage defaultMessage="Brand Name" />
            </TableCellHeader>
            <TableCell className={classes.colSignatory}>
              <FormattedMessage defaultMessage="Signatory" />
            </TableCell>
            <TableCellHeader
              direction={
                sort.sort === SellerListUrlSortField.email
                  ? getArrowDirection(sort.asc)
                  : undefined
              }
              arrowPosition="right"
              onClick={() => onSort(SellerListUrlSortField.email)}
              className={classes.colEmail}
            >
              <FormattedMessage defaultMessage="Email Address" />
            </TableCellHeader>
            <TableCell className={classes.colMobile}>
              <FormattedMessage defaultMessage="Mobile Number" />
            </TableCell>
            <TableCell className={classes.colStatus}>
              <FormattedMessage defaultMessage="Status" />
            </TableCell>
          </TableRow>
        </TableHead>
        <TableFooter>
          <TableRow>
            <TablePagination
              colSpan={colNumber}
              settings={settings}
              hasNextPage={
                pageInfo && !disabled ? pageInfo.hasNextPage : undefined
              }
              onNextPage={onNextPage}
              onUpdateListSettings={onUpdateListSettings}
              hasPreviousPage={
                pageInfo && !disabled ? pageInfo.hasPreviousPage : undefined
              }
              onPreviousPage={onPreviousPage}
            />
          </TableRow>
        </TableFooter>
        <TableBody>
          {renderCollection(
            sellers,
            (seller, index) => (
              <TableRow
                className={classes.tableRow}
                hover={!!seller}
                onClick={!!seller ? onRowClick(seller.id) : undefined}
                key={seller ? seller.id : "skeleton"}
              >
                <TableCell id={`sellerIDRow${index}`}>
                  {maybe<React.ReactNode>(() => seller.sellerId, <Skeleton />)}
                </TableCell>
                <TableCell id={`companyNameRow${index}`}>
                  {maybe<React.ReactNode>(() => seller.legalName, <Skeleton />)}
                </TableCell>
                <TableCell id={`brandNameRow${index}`}>
                  {maybe<React.ReactNode>(() => seller.name, <Skeleton />)}
                </TableCell>
                <TableCell id={`signatoryRow${index}`}>
                  {maybe<React.ReactNode>(
                    () => seller.signatoryName,
                    <Skeleton />
                  )}
                </TableCell>
                <TableCell id={`emailAddressRow${index}`}>
                  {maybe<React.ReactNode>(
                    () => seller.signatoryEmail,
                    <Skeleton />
                  )}
                </TableCell>
                <TableCell id={`mobileNumberRow${index}`}>
                  {maybe<React.ReactNode>(
                    () => seller.signatoryMobileNo,
                    <Skeleton />
                  )}
                </TableCell>
                <TableCell id={`statusRow${index}`}>
                  {maybe<React.ReactNode>(
                    () =>
                      seller?.status === "ACT"
                        ? "Active"
                        : seller?.status === "RVW"
                        ? "Under Review"
                        : seller?.status === "INA"
                        ? "Deactivated"
                        : seller?.status === "DCL"
                        ? "Decline"
                        : seller?.status,
                    <Skeleton />
                  )}
                </TableCell>
              </TableRow>
            ),
            () => (
              <TableRow>
                <TableCell colSpan={colNumber}>
                  <FormattedMessage defaultMessage="No Seller found" />
                </TableCell>
              </TableRow>
            )
          )}
        </TableBody>
      </ResponsiveTable>
    </>
  );
};

export default SellerList;
